import * as React from 'react';
import {
    Container, Box, Paper, TextField, InputAdornment, Button,
    Stack, IconButton, CircularProgress, Typography, Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { facturasget } from '../Componentes/FunctionLogin.js';
import { useTranslation } from 'react-i18next';
import InvoiceMetrics from './InvoiceMetrics';
import InvoiceCard from './InvoiceCard';
import EmptyState from './EmptyState';

const XLSX = require('xlsx');

export default function InvoicesList() {
    const [t] = useTranslation("global");

    // State
    const [searchTerm, setSearchTerm] = React.useState('');
    const [invoices, setInvoices] = React.useState([]);
    const [statusFilter, setStatusFilter] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const invoicesPerPage = 10;

    // Load invoices function
    const loadInvoices = async () => {
        setLoading(true);
        try {
            const result = await facturasget(searchTerm.trim());
            setInvoices(Array.isArray(result) ? result : []);
        } catch (error) {
            console.error('Error loading invoices:', error);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredInvoices);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Facturas');
        XLSX.writeFile(workbook, `facturas_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    // Filter invoices by search term
    // Filter invoices by search term and status
    const filteredInvoices = invoices.filter(invoice => {
        if (!invoice) return false;
        // 1. Filtrar por término de búsqueda
        const searchLower = searchTerm.toLowerCase().trim();
        const matchesSearch = searchLower === '' || (
            (invoice.idFactura || '').toLowerCase().includes(searchLower) ||
            (invoice.tipodedocumento || '').toLowerCase().includes(searchLower) ||
            (invoice.estado || '').toLowerCase().includes(searchLower)
        );

        // 2. Filtrar por estado seleccionado en las métricas
        const matchesStatus = !statusFilter || invoice.estado === statusFilter;

        return matchesSearch && matchesStatus;
    });

    // Pagination
    const totalPages = Math.ceil(filteredInvoices.length / invoicesPerPage);
    const paginatedInvoices = filteredInvoices.slice(
        (page - 1) * invoicesPerPage,
        page * invoicesPerPage
    );

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Search and Actions Bar */}
            <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <TextField
                        placeholder={t("facturas.buscar") || "Buscar facturas..."}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                loadInvoices();
                            }
                        }}
                        sx={{ flexGrow: 1, minWidth: '250px' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Button
                        variant="contained"
                        onClick={loadInvoices}
                        disabled={false} // El botón siempre estará habilitado
                        sx={{
                            backgroundColor: '#BD1F34',
                            '&:hover': { backgroundColor: '#9a1829' }
                        }}
                    >
                        {t("facturas.buscar") || "Buscar"}
                    </Button>

                    <IconButton
                        color="primary"
                        onClick={loadInvoices}
                        disabled={false} // El botón siempre estará habilitado
                        sx={{ backgroundColor: '#F3F4F6' }}
                    >
                        <RefreshIcon />
                    </IconButton>

                    <IconButton
                        color="primary"
                        onClick={handleExportExcel}
                        disabled={filteredInvoices.length === 0}
                        sx={{ backgroundColor: '#F3F4F6' }}
                    >
                        <FileDownloadIcon />
                    </IconButton>
                </Stack>
            </Paper>

            {/* Metrics Dashboard */}
            {invoices.length > 0 && (
                <InvoiceMetrics
                    invoices={invoices}
                    selectedFilter={statusFilter}
                    onFilterChange={(filter) => {
                        setStatusFilter(filter);
                        setPage(1); // Reset to first page on filter change
                    }}
                />
            )}

            {/* Help Card */}
            {invoices.length === 0 && !loading && (
                <Paper elevation={2} sx={{ p: 4, textAlign: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
                        {t("facturas.relacion") || "Estado de Cuenta"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                        {t("facturas.informacion") || "Ingresa el número de factura o término de búsqueda y presiona el botón Buscar"}
                    </Typography>
                </Paper>
            )}

            {/* Invoices List */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={60} sx={{ color: '#BD1F34' }} />
                </Box>
            ) : paginatedInvoices.length > 0 ? (
                <Box>
                    <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
                        {t("facturas.mostrando", { count: paginatedInvoices.length, total: filteredInvoices.length }) || `Mostrando ${paginatedInvoices.length} de ${filteredInvoices.length} facturas`}
                    </Typography>

                    <Stack spacing={2}>
                        {paginatedInvoices.map((invoice, index) => (
                            <InvoiceCard key={invoice.id || index} invoice={invoice} />
                        ))}
                    </Stack>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, value) => setPage(value)}
                                color="primary"
                                size="large"
                            />
                        </Box>
                    )}
                </Box>
            ) : invoices.length === 0 && !loading ? null : (
                <EmptyState
                    type="no-results"
                    onClear={() => {
                        setSearchTerm('');
                        setInvoices([]);
                    }}
                />
            )}
        </Container>
    );
}